import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import axios from 'axios'
import router from '@/router'

const apiURL: string = import.meta.env.VITE_API_URL + '/api'

const api = axios.create({
  baseURL: apiURL,
  withCredentials: true,
})

export const useAuthStore = defineStore('auth', () => {
  const ticket = ref<string | null>(localStorage.getItem('authTicket'))
  const currentUser = ref<any>(
    localStorage.getItem('authUser') ? JSON.parse(localStorage.getItem('authUser')!) : null
  )

  const isAuthenticated = computed(() => !!ticket.value && !!currentUser.value)

  // group names from the API (Admin, Supervisor, Staff, Manager), refreshed on every
  // ticket check; kept in storage so a page reload does not briefly lock admins out
  const roles = ref<string[]>(readRoles())
  const isAdmin = computed(() => roles.value.includes('Admin'))
  // Admin or Manager: the activity log, closing a period, master data
  const isManagement = computed(() => roles.value.includes('Admin') || roles.value.includes('Manager'))
  // Supervisor, Manager or Admin: the yearly and accumulated reports
  const isSupervisorUp = computed(() => isManagement.value || roles.value.includes('Supervisor'))

  function readRoles(): string[] {
    try {
      const stored = JSON.parse(localStorage.getItem('authRoles') || '[]')
      return Array.isArray(stored) ? stored : []
    } catch {
      return []
    }
  }

  function setRoles(next: string[]) {
    roles.value = next
    localStorage.setItem('authRoles', JSON.stringify(next))
  }

  function _persist(t: string, user: any) {
    ticket.value = t
    currentUser.value = user
    localStorage.setItem('authTicket', t)
    localStorage.setItem('authUser', JSON.stringify(user))
  }

  function _clear() {
    ticket.value = null
    currentUser.value = null
    localStorage.removeItem('authTicket')
    localStorage.removeItem('authUser')
    roles.value = []
    localStorage.removeItem('authRoles')
  }

  async function login(email: string, password: string): Promise<string | null> {
    try {
      const res = await api.post('/cas/tickets', { email, password })
      if (res.data?.status === 'success') {
        const data = res.data.data
        const t = data.ticket
        const user = data.auth?.user ?? null
        _persist(t, user)
        await checkAuth() // fetches the user's roles
        return null
      }
      return res.data?.message ?? 'Login failed'
    } catch {
      return 'Server error. Please try again.'
    }
  }

  async function logout() {
    if (ticket.value) {
      try {
        await api.delete('/cas/tickets/' + ticket.value)
      } catch {
        // ignore — clear local state regardless
      }
    }
    _clear()
    router.push('/login')
  }

  async function checkAuth(): Promise<boolean> {
    if (!ticket.value) {
      return false
    }
    try {
      const res = await api.get('/cas/validate/' + ticket.value)
      if (res.data?.status === 'success') {
        const user = res.data.data?.auth?.user ?? currentUser.value
        setRoles(Array.isArray(res.data.roles) ? res.data.roles : [])
        if (user) {
          currentUser.value = user
          localStorage.setItem('authUser', JSON.stringify(user))
          return true
        }
      }
    } catch {
      // network error — keep existing state if we have a user
      if (currentUser.value) return true
    }
    _clear()
    return false
  }

  async function updateProfile(payload: {
    first_name: string
    last_name: string
    email?: string
  }): Promise<string | null> {
    try {
      const res = await api.put('/cas/users/' + currentUser.value.uuid, payload)
      const errs = res.data?.data?.errors
      if (res.data?.status === 'success' && (!errs || Object.keys(errs).length === 0)) {
        currentUser.value = { ...currentUser.value, ...payload }
        localStorage.setItem('authUser', JSON.stringify(currentUser.value))
        return null
      }
      if (errs) {
        return Object.entries(errs)
          .map(([field, msg]) => `${field}: ${msg}`)
          .join(', ')
      }
      return res.data?.error ?? res.data?.message ?? 'Update failed'
    } catch {
      return 'Server error. Please try again.'
    }
  }

  async function changePassword(currentPassword: string, newPassword: string): Promise<string | null> {
    try {
      const res = await api.post('/cas/passwords', {
        email: currentUser.value.email,
        current_password: currentPassword,
        password: newPassword,
      })
      if (res.data?.status === 'success') return null
      if (res.data?.errors) return Object.values(res.data.errors).join(', ')
      return res.data?.message ?? 'Password change failed'
    } catch {
      return 'Server error. Please try again.'
    }
  }

  return {
    ticket,
    currentUser,
    isAuthenticated,
    roles,
    isAdmin,
    isManagement,
    isSupervisorUp,
    login,
    logout,
    checkAuth,
    updateProfile,
    changePassword,
  }
})
